import { useFocusRing } from '@react-aria/focus';
import { useTableCell, useTableSelectionCheckbox } from '@react-aria/table';
import { mergeProps } from '@react-aria/utils';
import { TableState } from '@react-stately/table';
import { GridNode } from '@react-types/grid';
import React, { useImperativeHandle, useRef } from 'react';

import { cx } from 'class-variance-authority';
import Checkbox from './Checkbox';
import { tableCellClassNames } from './styles';

type CellProps<T> = GridNode<T> & { parentKey?: React.Key };

interface Props<T> {
    cell: CellProps<T>;
    state: TableState<T>;
}

type NativeAttrs = Omit<React.HTMLAttributes<unknown>, keyof Props<any>>;

export type TableCheckboxCellProps<T = unknown> = Props<T> & NativeAttrs;

const TableCheckboxCell = React.forwardRef<HTMLTableCellElement, TableCheckboxCellProps>(
    ({ cell, state, ...props }, ref: React.Ref<HTMLTableCellElement | null>) => {
        const tableCellRef = useRef<HTMLTableCellElement | null>(null);

        useImperativeHandle(ref, () => tableCellRef?.current);

        const {
            gridCellProps
        }: {
            gridCellProps: Omit<React.HTMLAttributes<unknown>, keyof TableCheckboxCellProps<unknown>>;
        } = useTableCell({ node: cell }, state, tableCellRef);

        const { checkboxProps } = useTableSelectionCheckbox({ key: cell?.parentKey || cell.key }, state);
        const { isFocusVisible, focusProps } = useFocusRing();

        return (
            <td
                ref={tableCellRef}
                className={tableCellClassNames({
                    className: cx(props.className, 'tw-w-[55px]'),
                    isFocusVisible,
                    isSelected: checkboxProps.isSelected
                })}
                {...mergeProps(gridCellProps, focusProps, props)}
            >
                <Checkbox {...checkboxProps} />
            </td>
        );
    }
);

TableCheckboxCell.displayName = 'TableCheckboxCell';

export default TableCheckboxCell;
